// src/App.js
import { ConfigProvider, Layout } from 'antd';
import { HelmetProvider } from 'react-helmet-async';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.css';
import { UserProvider } from './UserContext';
import { AuthLayout } from './layouts/AuthLayout';
import { PushToSendPage } from './pages/agentLeads/pushToSend';
import { AgentSmsPage } from './pages/agentSms';
import { AppointmentReviewPage } from './pages/appointmentReview/page';
import BackfillStatusPage from './pages/client/[client_id]/backfill-status/page';
import { ClientSettingsPage } from './pages/client/[client_id]/settings/page';
import VerifyNewClientPage from './pages/client/[client_id]/verify-new/page';
import ClientsPage from './pages/client/page';
import { BroadcastMessagesPage } from './pages/admin/broadcast-messages/page';
import SupportTicketManagementPage from './pages/admin/support-tickets';
import { TwilioOnboardingPage } from './pages/client/[client_id]/twilio-onboarding/page';
import { TwilioOnboardingDashboardPage } from './pages/admin/twilio-onboarding/page';

import { AgentLeads } from './pages/agentLeads';
import { LeadEngagementsPage } from './pages/agentLeads/lead-engagements-page';
import { LeadEngagementDetailPage } from './pages/agentLeads/lead-engagement-detail-page';
import { LeadEngagementTestPage } from './pages/leadEngagementTest/page';
import { ErrorPage } from './pages/error';
import { HealthCheck } from './pages/health';
import HWRecordingPage from './pages/hw-recording';
import { LoftyLogin } from './pages/lofty';
import { Login } from './pages/login';
import { LoginNew } from './pages/login_new';
import { MoxiWorksLogin } from './pages/moxiworks';
import { OnboardingNewPage } from './pages/onboardingNew';
import OnboardingStatusPage from './pages/onboardingStatus';
import ClientSelect from './pages/page';
import { PrivacyStatement } from './pages/privacyStatement';
import { TaskReviewPage } from './pages/taskReview/page';
import { FubContainersPage } from './pages/resources/fub-containers';
import { ResourcesPage } from './pages/resources/page';
import { ConferenceReportPage } from './pages/conferenceReport/page';

const { Content } = Layout;

function App(): JSX.Element {
  const apiUrl = process.env.REACT_APP_API_URL || 'localhost:7071';
  return (
    <HelmetProvider>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#5b1eaf',
            colorInfo: '#5b1eaf',
            colorSuccess: '#52c41a',
            colorWarning: '#faad14',
            colorError: '#f5222d',
            colorTextBase: '#333333',
            fontFamily:
              '"Roboto", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            borderRadius: 4,
          },
          components: {
            Button: {
              borderRadius: 4,
              controlHeight: 40,
            },
            Card: {
              borderRadius: 8,
            },
            Typography: {
              titleMarginTop: 0,
              titleMarginBottom: 0,
              fontWeightStrong: 700,
            },
          },
        }}
      >
        <Router>
          <Layout
            className="App"
            style={{
              backgroundColor: '#f8fafc', // Lighter, modern background
              minHeight: '100vh',
            }}
          >
            <Content
              style={{
                padding: '0 2%',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Routes>
                <Route
                  element={
                    <UserProvider>
                      <AuthLayout />
                    </UserProvider>
                  }
                  path=""
                >
                  <Route
                    element={
                      <>
                        <ClientSelect />
                      </>
                    }
                    path="/"
                  />
                  <Route
                    element={
                      <>
                        <AgentSmsPage apiUrl={apiUrl} />
                      </>
                    }
                    path="/agentSms/:clientId/"
                  />

                  <Route
                    element={
                      <>
                        <TaskReviewPage apiUrl={apiUrl} />
                      </>
                    }
                    path="/task-review/:clientId/:recordingID"
                  />
                  <Route
                    element={
                      <>
                        <AppointmentReviewPage apiUrl={apiUrl} />
                      </>
                    }
                    path="/appointment-review/:clientId/:recordingID"
                  />
                  <Route
                    element={
                      <>
                        <OnboardingNewPage apiUrl={apiUrl} />
                      </>
                    }
                    path="/onboardingNew/:clientId"
                  />
                  <Route
                    element={<FubContainersPage />}
                    path="/resources/fub-containers"
                  />
                  <Route path="/client/">
                    <Route
                      element={
                        <>
                          <ClientsPage apiUrl={apiUrl} />
                        </>
                      }
                      path=""
                    />
                    <Route
                      element={
                        <>
                          <ClientSettingsPage apiUrl={apiUrl} />
                        </>
                      }
                      path=":clientId/settings"
                    />
                    <Route
                      element={
                        <>
                          <BackfillStatusPage apiUrl={apiUrl} />
                        </>
                      }
                      path=":clientId/backfill-status"
                    />
                    <Route
                      element={
                        <>
                          <VerifyNewClientPage apiUrl={apiUrl} />
                        </>
                      }
                      path=":clientId/verify-new"
                    />
                    <Route
                      element={<TwilioOnboardingPage apiUrl={apiUrl} />}
                      path=":clientId/twilio-onboarding"
                    />
                  </Route>
                  <Route
                    element={
                      <>
                        <OnboardingStatusPage apiUrl={apiUrl} />
                      </>
                    }
                    path="/onboarding/status/:clientId"
                  />
                  <Route
                    element={<AgentLeads apiUrl={apiUrl} />}
                    path="/agentLeads/:clientId/:agentId"
                  />
                  <Route
                    element={
                      <>
                        <BroadcastMessagesPage apiUrl={apiUrl} />
                      </>
                    }
                    path="/admin/broadcast-messages"
                  />
                  <Route
                    element={<SupportTicketManagementPage apiUrl={apiUrl} />}
                    path="/admin/support-tickets"
                  />
                  <Route
                    element={<TwilioOnboardingDashboardPage apiUrl={apiUrl} />}
                    path="/admin/twilio-onboarding"
                  />
                  <Route
                    element={<LeadEngagementTestPage apiUrl={apiUrl} />}
                    path="/admin/lead-engagement-test"
                  />
                  <Route
                    element={<LeadEngagementsPage apiUrl={apiUrl} />}
                    path="/lead-engagements/:clientId"
                  />
                  <Route
                    element={<LeadEngagementDetailPage apiUrl={apiUrl} />}
                    path="/lead-engagements/:clientId/:leadId"
                  />
                  <Route
                    element={<LeadEngagementsPage apiUrl={apiUrl} />}
                    path="/leadEngagement"
                  />
                  <Route element={<ResourcesPage />} path="/resources" />
                  <Route
                    element={
                      <ConferenceReportPage
                        apiUrl={process.env.REACT_APP_CONFERENCE_API_URL}
                      />
                    }
                    path="/admin/conference-report"
                  />
                </Route>
                {/* Unauthenticated routes */}
                <Route
                  element={
                    <>
                      <HWRecordingPage apiUrl={apiUrl} />
                    </>
                  }
                  path="/rec/:id"
                />
                <Route
                  element={
                    <>
                      <PrivacyStatement apiUrl={apiUrl} />
                    </>
                  }
                  path="/privacyStatement"
                />
                <Route
                  element={
                    <>
                      <ErrorPage apiUrl={apiUrl} />
                    </>
                  }
                  path="/error"
                />
                <Route
                  element={<HealthCheck apiUrl={apiUrl} />}
                  path="/health"
                />
                <Route
                  element={
                    <>
                      <PushToSendPage apiUrl={apiUrl} />
                    </>
                  }
                  path="/leads2/:clientId/:agentId/pts"
                />
                <Route
                  element={<Login apiUrl={apiUrl} />}
                  path="/login/:agentId"
                />
                <Route
                  element={<LoginNew apiUrl={apiUrl} />}
                  path="/login_new/:agentId"
                />
                <Route
                  element={<LoftyLogin apiUrl={apiUrl} />}
                  path="/lofty/:clientId"
                />
                <Route
                  element={<MoxiWorksLogin apiUrl={apiUrl} />}
                  path="/moxiworks/:clientId"
                />
              </Routes>
            </Content>
          </Layout>
        </Router>
      </ConfigProvider>
    </HelmetProvider>
  );
}

export default App;
